import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Loader2, AlertTriangle, Clock, CheckCircle, User,
  Wrench, MapPin, Calendar, MessageSquare, Send, Phone, Mail,
  FileText, PoundSterling, Download, UserPlus
} from "lucide-react";
import { API } from "@/App";

const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock },
  quote_pending: { label: "Quote Pending", color: "bg-orange-100 text-orange-700", icon: FileText },
  quote_required: { label: "Awaiting Approval", color: "bg-purple-100 text-purple-700", icon: PoundSterling },
  accepted: { label: "Accepted", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  scheduled: { label: "Scheduled", color: "bg-indigo-100 text-indigo-700", icon: Calendar },
  en_route: { label: "En Route", color: "bg-cyan-100 text-cyan-700", icon: MapPin },
  on_site: { label: "On Site", color: "bg-teal-100 text-teal-700", icon: Wrench },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle },
  quote_declined: { label: "Quote Declined", color: "bg-red-100 text-red-700", icon: AlertTriangle }
};

export const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const [loading, setLoading] = useState(true);
  const [jobData, setJobData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [availableContractors, setAvailableContractors] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState("");
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    fetchMessages();
    if (user.role === "admin" || user.role === "director" || user.role === "lettings_manager") {
      fetchAvailableContractors();
    }
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobData(response.data);
    } catch (error) {
      console.error("Error fetching job:", error);
      toast.error("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchAvailableContractors = async () => {
    try {
      const response = await axios.get(`${API}/contractors/with-portal-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableContractors(response.data.contractors || []);
    } catch (error) {
      console.error("Error fetching contractors:", error);
    }
  };

  const handleAssignContractor = async () => {
    if (!selectedContractor) {
      toast.error("Please select a contractor");
      return;
    }
    
    setAssigning(true);
    try {
      const response = await axios.post(
        `${API}/jobs/${jobId}/assign-contractor?contractor_id=${selectedContractor}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setSelectedContractor("");
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign contractor");
    } finally {
      setAssigning(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/messages`, 
        { job_id: jobId, content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage("");
      fetchMessages();
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${jobId.slice(-8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded");
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };

  const goBack = () => {
    const routes = {
      tenant: "/tenant",
      contractor: "/contractor",
      landlord: "/landlord",
      admin: "/admin"
    };
    navigate(routes[user.role] || "/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Job not found</p>
      </div>
    );
  }

  const { job, issue, tenant, contractor } = jobData;
  const status = statusConfig[job?.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={goBack} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Job Details</p>
              <h1 className="text-xl font-semibold font-outfit">#{jobId?.slice(-8).toUpperCase()}</h1>
            </div>
            <Badge className={status.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Issue Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Issue Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Type</p>
                <p className="font-medium">{issue?.issue_type}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Severity</p>
                <Badge variant="outline" className="capitalize">{issue?.severity}</Badge>
              </div>
              {issue?.property_postcode && (
                <div>
                  <p className="text-sm text-muted-foreground">Postcode</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {issue.property_postcode}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Reported</p>
                <p className="font-medium">
                  {new Date(issue?.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground mb-1">Description</p>
              <p className="bg-slate-50 rounded-lg p-3">{issue?.description}</p>
            </div>

            {issue?.is_emergency && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="text-red-700 font-medium">Emergency Issue</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quote & Payment */}
        {job?.quote_amount && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-green-500" />
                Quote & Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Quote Amount</span>
                <span className="text-2xl font-bold">£{job.quote_amount.toFixed(2)}</span>
              </div>
              {job.quote_description && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Description</p>
                  <p className="bg-slate-50 rounded-lg p-3 italic">"{job.quote_description}"</p>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-muted-foreground">Status</span>
                <Badge className={job.quote_approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                  {job.quote_approved ? "Approved" : "Pending Approval"}
                </Badge>
              </div>
              {job.auto_approved && (
                <p className="text-sm text-green-600">✓ Auto-approved (under threshold)</p>
              )}
              
              {job.status === "completed" && user.role === "landlord" && (
                <Button onClick={handleDownloadInvoice} className="w-full mt-2">
                  <Download className="w-4 h-4 mr-2" />
                  Download Invoice
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Scheduled Time */}
        {job?.scheduled_time && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Appointment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-blue-100 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-blue-600">
                    {new Date(job.scheduled_time).getDate()}
                  </span>
                  <span className="text-xs text-blue-600">
                    {new Date(job.scheduled_time).toLocaleString('default', { month: 'short' })}
                  </span>
                </div>
                <div>
                  <p className="font-medium">
                    {new Date(job.scheduled_time).toLocaleDateString('en-GB', { 
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </p>
                  <p className="text-muted-foreground">
                    {new Date(job.scheduled_time).toLocaleTimeString([], { 
                      hour: '2-digit', minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* People */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Tenant */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-4 h-4" />
                Tenant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{tenant?.name}</p>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {tenant?.email}
                </p>
                {tenant?.phone && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {tenant.phone}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contractor */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Contractor
              </CardTitle>
            </CardHeader>
            <CardContent>
              {contractor ? (
                <>
                  <p className="font-medium">{contractor.name}</p>
                  {contractor.contractor_profile?.company_name && (
                    <p className="text-sm text-muted-foreground">
                      {contractor.contractor_profile.company_name}
                    </p>
                  )}
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {contractor.email}
                    </p>
                    {contractor.phone && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {contractor.phone}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <p className="text-muted-foreground">Not yet assigned</p>
                  {(user.role === "admin" || user.role === "director" || user.role === "lettings_manager") && (
                    <div className="pt-3 border-t space-y-3">
                      <p className="text-sm font-medium text-slate-700">Assign Contractor</p>
                      <Select value={selectedContractor} onValueChange={setSelectedContractor}>
                        <SelectTrigger data-testid="contractor-select">
                          <SelectValue placeholder="Select a contractor..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableContractors.map((c) => (
                            <SelectItem key={c.user_id} value={c.user_id}>
                              {c.name} {c.specialty ? `(${c.specialty})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        onClick={handleAssignContractor}
                        disabled={!selectedContractor || assigning}
                        className="w-full"
                        data-testid="assign-contractor-btn"
                      >
                        {assigning ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Assigning...
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Assign Contractor
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Messages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Messages ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {messages.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No messages yet</p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {messages.map((msg, idx) => (
                  <div 
                    key={msg.message_id || idx}
                    className={`p-3 rounded-lg ${
                      msg.sender_id === user.user_id 
                        ? 'bg-primary text-white ml-8' 
                        : 'bg-slate-100 mr-8'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${
                        msg.sender_id === user.user_id ? 'text-white/80' : 'text-muted-foreground'
                      }`}>
                        {msg.sender_name} ({msg.sender_role})
                      </span>
                      <span className={`text-xs ${
                        msg.sender_id === user.user_id ? 'text-white/60' : 'text-muted-foreground'
                      }`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Send Message */}
            <div className="flex gap-2 pt-2 border-t">
              <Textarea
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="min-h-[60px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Button 
                onClick={handleSendMessage} 
                disabled={sendingMessage || !newMessage.trim()}
                className="self-end"
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Timeline placeholder */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-500" />
              Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 mt-2"></div>
                <div>
                  <p className="font-medium">Job Created</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(job?.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              {job?.scheduled_time && (
                <div className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                  <div>
                    <p className="font-medium">Appointment Scheduled</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(job.scheduled_time).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
              {job?.status === "completed" && (
                <div className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2"></div>
                  <div>
                    <p className="font-medium">Job Completed</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(job.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
